import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';

const FALLBACK_FEES = {
  academic_year: '2026-27',
  last_updated: 'Session 2026–27',
  pdf_url: '/uploads/docs/Fee-Structure-2026-27.pdf',
  intro_badges: [
    'Registration Fee Rs 1,000/- (One-Time)',
    'Admission Fee Rs 15,000/- (One-Time)',
    'Caution Money (Refundable) Rs 10,000/-',
    'Transport Charges for 11 Months Only',
  ],
  main_fee: {
    heading: 'Fee Structure (Session 2026–27)',
    note: 'Tuition fees are payable monthly/quarterly. One-time charges are applicable at the time of admission.',
    columns: [
      'Particular',
      'Nursery–UKG',
      'I-V',
      'VI-VIII',
      'IX-X',
      'XI-XII (Hum, Comm)',
      'XI-XII (Science)',
    ],
    rows: [
      {
        label: 'Registration Fee (One Time)',
        values: ['1,000/-', '1,000/-', '1,000/-', '1,000/-', '1,000/-', '1,000/-'],
      },
      {
        label: 'Admission Fee (One Time)',
        values: ['15,000/-', '15,000/-', '15,000/-', '15,000/-', '15,000/-', '15,000/-'],
      },
      {
        label: 'Caution Money (Refundable)',
        values: ['10,000/-', '10,000/-', '10,000/-', '10,000/-', '10,000/-', '10,000/-'],
      },
      {
        label: 'Annual Charge',
        values: ['18,000/-', '20,000/-', '20,000/-', '20,000/-', '20,000/-', '20,000/-'],
      },
      {
        label: 'Tuition Fee (Monthly)',
        values: ['4,360/-', '4,800/-', '5,200/-', '5,700/-', '6,000/-', '6,500/-'],
      },
    ],
  },
  transport: {
    heading: 'Transport Charges (Distance-Based)',
    note: 'Billed on a distance basis and collected only for 11 months in an academic session (no transport fee for June).',
    columns: ['Commute Distance Slab', 'Monthly Charge', 'Quarterly Equivalent'],
    rows: [
      { label: '0 – 4 KM', values: ['₹ 2,400/-', '₹ 7,200/-'] },
      { label: '4 – 6 KM', values: ['₹ 2,700/-', '₹ 8,100/-'] },
      { label: '6 – 8 KM', values: ['₹ 3,000/-', '₹ 9,000/-'] },
      { label: '8 – 10 KM', values: ['₹ 3,300/-', '₹ 9,900/-'] },
      { label: 'Above 10 KM', values: ['₹ 3,800/-', '₹ 11,400/-'] },
    ],
  },
  notes: [
    'The Composite School Fee covers high-quality academic instruction, smart classrooms, co-curricular sports, and state-of-the-art laboratory access.',
    'Tuition fee is payable as per the communicated monthly or quarterly schedule by the 10th of the calendar month.',
    'Transport charges are collected only for 11 months of the academic session (no fee charged for the summer vacation month of June).',
    'Caution money is refundable upon clearance of all school dues with one month prior notice in writing.',
  ],
  rebates: [
    'First Sibling Concession: Special concession on admission charges for the younger sibling.',
    'Second Sibling Concession: Additional concession as per St. Andrews World School policy.',
    'Sibling concessions remain applicable while the elder sibling is actively studying at SAWS Sonipat.',
  ],
  withdrawal:
    'Withdrawal request must be submitted to the admissions office with 1 month prior written notice on or before the 10th of the calendar month, failing which notice period fee equal to one month shall be adjusted with the caution deposit.',
  paymentInfo: [
    'Online remittance facility is available via net banking, UPI, and authorized school accounts.',
    'Fee payments arriving after the due date shall attract a nominal late fee penalty of INR 50/- per day.',
    'Always mention the student admission number and name on payment receipts for prompt verification.',
  ],
};

const openMerritoPopup = (e) => {
  if (e && e.preventDefault) e.preventDefault();
  if (typeof window.openEnquiryPopup === 'function') {
    window.openEnquiryPopup();
  } else {
    const btn = document.querySelector('.npfWidgetButton.npfWidget-ab76b73b357938fdd5c126d3bf550af2') || document.querySelector('.npfWidgetButton');
    if (btn) {
      btn.click();
    } else {
      window.location.href = '/admission#enquire';
    }
  }
};

const FeeTable = ({ icon, title, note, columns, rows, action }) => (
  <div className="mb-12 overflow-hidden border border-gray-200 bg-white shadow-sm transition-all hover:border-[#d8a85f] hover:shadow-lg">
    <div className="p-6 md:p-8 bg-white border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="flex items-start md:items-center gap-4">
        <div className="w-12 h-12 flex items-center justify-center bg-[#f9f8f4] text-[#d8a85f] shrink-0 border border-[#d8a85f]/30">
          <i className={`fas ${icon} text-xl`} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-[#1b3160]" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
            {title}
          </h3>
          {note && <p className="text-[15px] text-gray-500 mt-1 leading-relaxed">{note}</p>}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[15px] min-w-[760px]">
        <thead>
          <tr className="bg-[#1b3160] text-white">
            {columns.map((col, i) => (
              <th
                key={i}
                className="px-5 py-4 text-left font-semibold whitespace-nowrap border-r border-[#1b3160]/20 last:border-0"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <tr key={idx} className="transition-colors hover:bg-gray-50">
              <td className="px-5 py-4 font-bold text-[#1b3160] whitespace-nowrap bg-[#f9f8f4] border-r border-gray-100">
                {row.label}
              </td>
              {row.values.map((v, vi) => (
                <td key={vi} className="px-5 py-4 whitespace-nowrap font-medium text-gray-700">
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const InfoCard = ({ icon, title, points }) => (
  <div className="border border-gray-200 bg-white p-6 md:p-8 transition-all hover:-translate-y-1 hover:border-[#d8a85f] hover:shadow-lg h-full">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-12 h-12 flex items-center justify-center bg-[#f9f8f4] text-[#d8a85f] shrink-0 border border-[#d8a85f]/30">
        <i className={`fas ${icon} text-xl`} />
      </div>
      <h3 className="text-xl font-bold text-[#1b3160]" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
        {title}
      </h3>
    </div>
    <ul className="space-y-4 list-none p-0 m-0">
      {points.map((p, i) => (
        <li key={i} className="flex gap-3 text-gray-600 text-[15px] leading-relaxed">
          <i className="fas fa-circle-check mt-1 text-[#d8a85f] text-[15px] shrink-0" />
          <span>{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

const PolicyCard = ({ icon, title, content, isList = false }) => (
  <div className="border border-gray-200 bg-white p-6 md:p-8 transition-all hover:-translate-y-1 hover:border-[#d8a85f] hover:shadow-lg h-full">
    <div className="flex items-center gap-4 mb-6">
      <div className="w-12 h-12 flex items-center justify-center bg-[#1b3160] text-white shrink-0">
        <i className={`fas ${icon} text-xl`} />
      </div>
      <h3 className="text-xl font-bold text-[#1b3160]" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
        {title}
      </h3>
    </div>
    {isList ? (
      <ul className="space-y-4 list-none p-0 m-0">
        {content.map((p, i) => (
          <li key={i} className="flex gap-3 text-gray-600 text-[15px] leading-relaxed">
            <i className="fas fa-circle-check mt-1 text-[#d8a85f] text-[15px] shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-gray-600 text-[15px] leading-relaxed">{content}</p>
    )}
  </div>
);

const Fees = () => {
  const [pageData, setPageData] = useState(null);
  const [fees, setFees] = useState(FALLBACK_FEES);

  useEffect(() => {
    API.get('/pages/public/fees')
      .then((res) => {
        const d = res.data?.data;
        if (d) {
          setPageData(d);
          if (d.sections && typeof d.sections === 'object') {
            setFees((prev) => ({
              ...prev,
              ...d.sections,
              main_fee: {
                ...prev.main_fee,
                ...(d.sections.main_fee || {}),
                rows: d.sections.main_fee?.rows?.length ? d.sections.main_fee.rows : prev.main_fee.rows,
                columns: d.sections.main_fee?.columns?.length ? d.sections.main_fee.columns : prev.main_fee.columns,
              },
              transport: {
                ...prev.transport,
                ...(d.sections.transport || {}),
                rows: d.sections.transport?.rows?.length ? d.sections.transport.rows : prev.transport.rows,
                columns: d.sections.transport?.columns?.length ? d.sections.transport.columns : prev.transport.columns,
              },
              intro_badges: d.sections.intro_badges?.length ? d.sections.intro_badges : prev.intro_badges,
              notes: d.sections.notes?.length ? d.sections.notes : prev.notes,
              rebates: d.sections.rebates?.length ? d.sections.rebates : prev.rebates,
              paymentInfo: d.sections.paymentInfo?.length ? d.sections.paymentInfo : prev.paymentInfo,
            }));
          }
        }
      })
      .catch(() => {});
  }, []);

  const bannerImgSrc = pageData?.banner_image
    ? (pageData.banner_image.startsWith('http') ? pageData.banner_image : `${UPLOADS_URL}/${pageData.banner_image}`)
    : '/images/page-banners/admission-guidliness.jpg';

  const banner = fees.banner || {};
  const pdfLink = fees.pdf_url
    ? (fees.pdf_url.startsWith('http') || fees.pdf_url.startsWith('/') ? fees.pdf_url : `${UPLOADS_URL}/${fees.pdf_url}`)
    : '/uploads/docs/Fee-Structure-2026-27.pdf';

  return (
    <>
      <SEO
        slug="fees"
        title={banner.title ? `${banner.title} | St. Andrews World School, Sonipat` : "Fee Structure (Session 2026–27) | St. Andrews World School, Sonipat"}
        description="Comprehensive School Fee Structure (2026-27), tuition fees, transport slabs, and admission charges for St. Andrews World School, Sonipat."
        keywords="Fee Structure Sonipat, CBSE School Fees Sonipat, St Andrews Sonipat Fees, Admission Fees 2026-27"
      />

      {/* ─── HERO ─────────────────────────────────────────── */}
      <PageBanner
        title={banner.title || pageData?.title || "Fee Structure"}
        subtitle={banner.subtitle || "Admissions · Sonipat"}
        breadcrumb={banner.breadcrumb || "FEE STRUCTURE"}
        bgImage={bannerImgSrc}
      />

      {/* ─── FEE CONTENT ─────────────────────────────────────────── */}
      <section className="bg-[#f9f8f4] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {/* Header & Badges */}
          <div className="mb-14 grid gap-5 md:grid-cols-2 md:items-end">
            <div>
              <span className="kicker block text-left uppercase tracking-widest text-[15px] font-bold text-gray-500 mb-2">
                {fees.academic_year ? `Session ${fees.academic_year}` : 'Session 2026–27'}
              </span>
              <h2 className="text-4xl text-[#1b3160] md:text-5xl font-bold leading-tight" style={{ fontFamily: '"Cormorant Garamond", "Playfair Display", serif' }}>
                Key Fee Inclusions
              </h2>
            </div>
            <div className="justify-self-start md:justify-self-end">
              <a
                href={pdfLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1b3160] text-white hover:bg-[#1b3160]/90 transition-colors px-6 py-3 text-sm font-semibold shadow"
              >
                <i className="fas fa-file-pdf text-[#d8a85f]" />
                <span>Download Fee Structure (PDF)</span>
              </a>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-14">
            {(fees.intro_badges || []).map((b, i) => (
              <div key={i} className="flex gap-3.5 p-5 bg-white border border-[#d8a85f]/30 items-start shadow-xs">
                <i className="fas fa-circle-check text-[#d8a85f] mt-1 shrink-0" />
                <span className="text-[15px] font-medium text-[#1b3160] leading-relaxed">
                  {b}
                </span>
              </div>
            ))}
          </div>

          {/* MAIN FEE TABLE */}
          <FeeTable
            icon="fa-file-invoice-dollar"
            title={fees.main_fee.heading}
            note={fees.main_fee.note}
            columns={fees.main_fee.columns}
            rows={fees.main_fee.rows}
            action={
              <a
                href={pdfLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#1b3160] text-[#1b3160] hover:bg-[#1b3160] hover:text-white transition-colors px-4 py-2 text-xs font-bold uppercase tracking-wider"
              >
                <i className="fas fa-arrow-down-to-line" />
                <span>PDF Download</span>
              </a>
            }
          />

          {/* TRANSPORT FEE TABLE */}
          <FeeTable
            icon="fa-bus"
            title={fees.transport.heading}
            note={fees.transport.note}
            columns={fees.transport.columns}
            rows={fees.transport.rows}
          />

          {/* POLICY & GUIDELINE CARDS */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <InfoCard icon="fa-circle-info" title="Important Fee Policies & Notes" points={fees.notes} />
            <InfoCard icon="fa-percent" title="Sibling Concessions & Rebates" points={fees.rebates} />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <PolicyCard icon="fa-file-contract" title="Guideline on Withdrawal" content={fees.withdrawal} />
            <PolicyCard icon="fa-credit-card" title="Payment & Accounts Support" content={fees.paymentInfo} isList={true} />
          </div>

          {/* BOTTOM CTA BANNER */}
          <div className="bg-[#1b3160] text-white p-8 md:p-12 border border-[#1b3160] shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d8a85f] block mb-2">
                Need Assistance?
              </span>
              <h3 className="text-2xl md:text-3xl font-bold font-serif leading-tight text-white">
                Have questions regarding fees, installments or transport routes?
              </h3>
              <p className="text-white/80 text-[15px] mt-2 leading-relaxed">
                Our admissions and accounts counselors are available Monday to Saturday to guide you through the process.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                onClick={openMerritoPopup}
                className="inline-flex items-center justify-center gap-2 bg-[#d8a85f] text-[#1b3160] hover:bg-[#d8a85f]/90 transition-colors px-7 py-3 text-sm font-bold uppercase tracking-wider shadow cursor-pointer"
              >
                Apply Now <i className="fas fa-arrow-right text-xs" />
              </button>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white hover:text-[#1b3160] transition-colors px-6 py-3 text-sm font-semibold"
              >
                Contact Admissions
              </Link>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default Fees;